import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, AlertCircle, CheckCircle, Coffee, LogOut } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { usePayrollHoursText } from '../hooks/usePayrollHoursText';
import { supabase } from '../lib/supabase';

import toast from 'react-hot-toast';
import {
  clockIn,
  clockOut,
  startBreak,
  endBreak,
  getTimeclockStatus,
  isOnBreak,
  workedMs,
  type TimeclockStatus,
} from '../lib/timeclock';

interface ResourceStatus {
  resourceId: string;
  resourceName: string;
  isClockedIn: boolean;
  isOnBreak: boolean;
  currentEntryId: string | null;
  clockInTime: string | null;
  breakStartTime: string | null;
  breakEndTime: string | null;
  totalMinutes: number;
  totalBreakMinutes: number;
}

export default function ClockInPincode() {
  const { selectedBusiness } = useBusiness();
  const navigate = useNavigate();
  const [passcode, setPasscode] = useState('');
  const [loading, setLoading] = useState(false);
  const [clockInUrlEnabled, setClockInUrlEnabled] = useState(false);
  const [checkingSetting, setCheckingSetting] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [resourceStatus, setResourceStatus] = useState<ResourceStatus | null>(null);
  const breakLabel = usePayrollHoursText([resourceStatus?.totalBreakMinutes || 0]);
  const [validated, setValidated] = useState(false);
  const [elapsedTime, setElapsedTime] = useState('00:00:00');
  const [currentBreakTime, setCurrentBreakTime] = useState('00:00:00');

  // Check if clock in URL setting is enabled
  useEffect(() => {
    const checkSetting = async () => {
      if (!selectedBusiness?.id || !supabase) {
        setCheckingSetting(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'payroll',
            p_setting_key: 'clock_in_url_enabled',
            p_default_value: false
          });

        if (error) throw error;

        if (data === true) {
          setClockInUrlEnabled(true);
        } else {
          // Setting is disabled, redirect or show error
          setError('Clock in via URL is not enabled. Please enable it in T-Sheets app settings.');
        }
      } catch (err) {
        console.error('Error checking clock in URL setting:', err);
        setError('Failed to check settings. Please try again.');
      } finally {
        setCheckingSetting(false);
      }
    };

    checkSetting();
  }, [selectedBusiness?.id]);

  // Update elapsed time in real-time when clocked in
  useEffect(() => {
    if (!resourceStatus || !resourceStatus.isClockedIn || !resourceStatus.clockInTime) {
      setElapsedTime('00:00:00');
      setCurrentBreakTime('00:00:00');
      return;
    }

    const updateTimes = () => {
      const now = new Date();
      const clockInTime = new Date(resourceStatus.clockInTime!);
      
      // Calculate total elapsed time
      let totalMs = now.getTime() - clockInTime.getTime();
      
      // Subtract completed break time
      if (resourceStatus.breakStartTime && resourceStatus.breakEndTime) {
        const breakStart = new Date(resourceStatus.breakStartTime);
        const breakEnd = new Date(resourceStatus.breakEndTime);
        totalMs -= (breakEnd.getTime() - breakStart.getTime());
      }
      
      // Subtract current break if on break
      if (resourceStatus.isOnBreak && resourceStatus.breakStartTime) {
        const breakStart = new Date(resourceStatus.breakStartTime);
        const currentBreakMs = now.getTime() - breakStart.getTime();
        totalMs -= currentBreakMs;
        
        // Calculate current break time
        const breakHours = Math.floor(currentBreakMs / (1000 * 60 * 60));
        const breakMinutes = Math.floor((currentBreakMs % (1000 * 60 * 60)) / (1000 * 60));
        const breakSeconds = Math.floor((currentBreakMs % (1000 * 60)) / 1000);
        setCurrentBreakTime(
          `${breakHours.toString().padStart(2, '0')}:${breakMinutes.toString().padStart(2, '0')}:${breakSeconds.toString().padStart(2, '0')}`
        );
      } else {
        setCurrentBreakTime('00:00:00');
      }
      
      // Format total elapsed time
      const hours = Math.floor(totalMs / (1000 * 60 * 60));
      const minutes = Math.floor((totalMs % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((totalMs % (1000 * 60)) / 1000);
      
      setElapsedTime(
        `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      );
    };

    updateTimes();
    const intervalId = setInterval(updateTimes, 1000);

    return () => clearInterval(intervalId);
  }, [resourceStatus]);

  // Helper function to format date/time
  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const handlePasscodeChange = (value: string) => {
    // Only allow numeric input and limit to 4 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 4);
    setPasscode(numericValue);
    setError(null);
    setSuccess(false);
    // Reset validation when passcode changes
    if (validated && numericValue.length < 4) {
      setValidated(false);
      setResourceStatus(null);
    }
  };

  const toResourceStatus = (status: TimeclockStatus): ResourceStatus => {
    const entry = status.entry;
    const onBreak = isOnBreak(entry);
    const now = new Date();
    let totalBreakMinutes = 0;
    if (entry?.break_start) {
      const breakEnd = entry.break_end ? new Date(entry.break_end) : now;
      totalBreakMinutes = Math.round((breakEnd.getTime() - new Date(entry.break_start).getTime()) / 60000);
    }
    return {
      resourceId: status.resource.id,
      resourceName: `${status.resource.first_name ?? ''} ${status.resource.last_name ?? ''}`.trim(),
      isClockedIn: !!entry,
      isOnBreak: onBreak,
      currentEntryId: entry?.id ?? null,
      clockInTime: entry?.clock_in ?? null,
      breakStartTime: entry?.break_start ?? null,
      breakEndTime: entry?.break_end ?? null,
      totalMinutes: entry ? Math.round(workedMs(entry, now) / 60000) : 0,
      totalBreakMinutes,
    };
  };

  const refreshStatus = async () => {
    if (!selectedBusiness?.id || passcode.length !== 4) return;
    try {
      const status = await getTimeclockStatus(selectedBusiness.id, { passcode });
      setResourceStatus(toResourceStatus(status));
    } catch (err) {
      console.error('Error refreshing status:', err);
    }
  };

  const validatePasscode = async () => {
    if (!selectedBusiness?.id) return;
    if (passcode.length !== 4) {
      setError('Please enter a 4-digit passcode');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const status = await getTimeclockStatus(selectedBusiness.id, { passcode });
      setResourceStatus(toResourceStatus(status));
      setValidated(true);
      setSuccess(true);
    } catch (err) {
      console.error('Error validating passcode:', err);
      setError(err instanceof Error ? err.message : 'Failed to validate passcode. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Every kiosk action: run it through the shared engine, refresh, confirm, then
  // return to the passcode screen for the next person.
  const runKioskAction = async (
    action: () => Promise<unknown>,
    successMessage: string,
    fallbackError: string,
    resetDelayMs = 2000,
  ) => {
    if (!selectedBusiness?.id || !resourceStatus) return;
    setLoading(true);
    setError(null);
    try {
      await action();
      await refreshStatus();
      toast.success(successMessage);
      setSuccess(true);
      setTimeout(() => {
        handleReset();
        setSuccess(false);
      }, resetDelayMs);
    } catch (err) {
      console.error(fallbackError, err);
      setError(err instanceof Error ? err.message : fallbackError);
    } finally {
      setLoading(false);
    }
  };

  const handleClockIn = () => runKioskAction(
    () => clockIn(selectedBusiness!.id, { passcode, source: 'kiosk', skipLocation: true }),
    'Clocked in successfully!',
    'Failed to clock in. Please try again.',
  );

  const handleBreakIn = () => runKioskAction(
    () => startBreak(selectedBusiness!.id, { passcode }),
    'Break started',
    'Failed to start break. Please try again.',
  );

  const handleBreakOut = () => runKioskAction(
    () => endBreak(selectedBusiness!.id, { passcode }),
    'Break ended',
    'Failed to end break. Please try again.',
  );

  const handleClockOut = () => runKioskAction(
    () => clockOut(selectedBusiness!.id, { passcode, source: 'kiosk', skipLocation: true }),
    'Clocked out successfully!',
    'Failed to clock out. Please try again.',
    1000,
  );

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && passcode.length === 4 && !loading && !validated) {
      validatePasscode();
    }
  };

  const handleReset = () => {
    setPasscode('');
    setValidated(false);
    setResourceStatus(null);
    setError(null);
    setSuccess(false);
  };

  if (checkingSetting) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Checking settings...</p>
        </div>
      </div>
    );
  }

  if (!clockInUrlEnabled) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Clock In URL Not Enabled</h1>
          <p className="text-gray-600 mb-4">
            Clock in via URL is not enabled for this business. Please enable it in T-Sheets app settings.
          </p>
          <button
            onClick={() => navigate('/payroll')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to T-Sheets
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        {!validated ? (
          <>
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="h-8 w-8 text-blue-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Clock In</h1>
              <p className="text-gray-600">Enter your 4-digit passcode</p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Passcode
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{4}"
                maxLength={4}
                value={passcode}
                onChange={(e) => handlePasscodeChange(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="0000"
                className="w-full px-4 py-4 text-2xl text-center tracking-widest bg-gray-50 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                style={{ fontSize: '24px', letterSpacing: '0.5em' }}
                autoFocus
              />
            </div>

            <button
              onClick={validatePasscode}
              disabled={passcode.length !== 4 || loading}
              className="w-full py-4 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-lg"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                  Validating...
                </span>
              ) : (
                'Continue'
              )}
            </button>
          </>
        ) : resourceStatus ? (
          <>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Welcome, {resourceStatus.resourceName.split(' ')[0]}!</h1>
              <p className="text-gray-600 mb-4">
                {resourceStatus.isClockedIn 
                  ? resourceStatus.isOnBreak 
                    ? 'Currently on break' 
                    : 'Currently clocked in'
                  : 'Not clocked in'}
              </p>
              
              {/* Time Information */}
              {resourceStatus.isClockedIn && resourceStatus.clockInTime && (
                <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Clocked in:</span>
                    <span className="text-sm font-semibold text-gray-900">{formatDateTime(resourceStatus.clockInTime)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Time worked:</span>
                    <span className="text-lg font-bold text-blue-600">{elapsedTime}</span>
                  </div>
                  {resourceStatus.isOnBreak && resourceStatus.breakStartTime && (
                    <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                      <span className="text-sm text-gray-600">Break started:</span>
                      <span className="text-sm font-semibold text-gray-900">{formatDateTime(resourceStatus.breakStartTime)}</span>
                    </div>
                  )}
                  {resourceStatus.isOnBreak && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Break time:</span>
                      <span className="text-sm font-semibold text-yellow-600">{currentBreakTime}</span>
                    </div>
                  )}
                  {resourceStatus.breakStartTime && resourceStatus.breakEndTime && !resourceStatus.isOnBreak && (
                    <>
                      <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                        <span className="text-sm text-gray-600">Break:</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {formatDateTime(resourceStatus.breakStartTime)} - {formatDateTime(resourceStatus.breakEndTime)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Total break time:</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {breakLabel(resourceStatus.totalBreakMinutes)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {success && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                <p className="text-sm text-green-700">Action completed successfully!</p>
              </div>
            )}

            <div className="space-y-3">
              {!resourceStatus.isClockedIn ? (
                <button
                  onClick={handleClockIn}
                  disabled={loading}
                  className="w-full py-4 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-lg flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Clocking in...
                    </>
                  ) : (
                    <>
                      <Clock className="h-5 w-5" />
                      Clock In
                    </>
                  )}
                </button>
              ) : (
                <>
                  {resourceStatus.isOnBreak ? (
                    <button
                      onClick={handleBreakOut}
                      disabled={loading}
                      className="w-full py-4 bg-yellow-600 text-white rounded-xl font-semibold hover:bg-yellow-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-lg flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          Ending break...
                        </>
                      ) : (
                        <>
                          <Coffee className="h-5 w-5" />
                          Break Out
                        </>
                      )}
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={handleBreakIn}
                        disabled={loading}
                        className="w-full py-4 bg-yellow-600 text-white rounded-xl font-semibold hover:bg-yellow-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-lg flex items-center justify-center gap-2"
                      >
                        {loading ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            Starting break...
                          </>
                        ) : (
                          <>
                            <Coffee className="h-5 w-5" />
                            Break In
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleClockOut}
                        disabled={loading}
                        className="w-full py-4 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-lg flex items-center justify-center gap-2"
                      >
                        {loading ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            Clocking out...
                          </>
                        ) : (
                          <>
                            <LogOut className="h-5 w-5" />
                            Clock Out
                          </>
                        )}
                      </button>
                    </>
                  )}
                </>
              )}
            </div>

            <div className="mt-6 text-center">
              <button
                onClick={handleReset}
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Use Different Passcode
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

